// AuthGuard — gate component for authenticated routes.
//
// Flow:
//   1. First mount in the session → trigger `fetchMe()` to hydrate the store.
//   2. While loading → render a centered <Spinner/>.
//   3. Transient failure → keep the protected URL and offer a retry.
//   4. Confirmed missing session → redirect to /login via react-router Navigate.
//   5. User present → render children.
//
// The route tree (see ../../router.tsx) wraps every authenticated path in
// this guard so a stale / missing cookie always lands the user on the
// login page.

import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Result } from 'antd';
import { Navigate, useLocation } from 'react-router-dom';

import { useTranslation } from '@govard/i18n-core/react';
import { Spinner } from '@govard/ui-primitives';

import { useCurrentUser, useScopeStore } from '../scope/useCurrentScope.js';

export interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps): ReactElement {
  const user = useCurrentUser();
  const isLoading = useScopeStore((state) => state.isLoading);
  const error = useScopeStore((state) => state.error);
  const fetchMe = useScopeStore((state) => state.fetchMe);
  const location = useLocation();
  const { t } = useTranslation();
  const [didAttempt, setDidAttempt] = useState(() => user !== null);

  useEffect(() => {
    if (user !== null) {
      return;
    }

    if (didAttempt) {
      return;
    }

    setDidAttempt(true);
    void fetchMe();
  }, [user, didAttempt, fetchMe]);

  if (user === null && (!didAttempt || isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (user === null && error !== null) {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <Result
          status="warning"
          title={t('crm.auth.guard.unavailable_title')}
          subTitle={t('crm.auth.guard.unavailable_description')}
          extra={
            <Button
              type="primary"
              aria-label={t('crm.shared.retry')}
              icon={<ReloadOutlined />}
              onClick={() => void fetchMe()}
            >
              {t('crm.shared.retry')}
            </Button>
          }
        />
      </main>
    );
  }

  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
